import { useCallback, useEffect } from 'react'

import { useVirtualKeyboardVisibilityRef } from '@bettermode/common/keyboard'
import { ChatComposer as Composer } from '@tribeplatform/react-components/Composer'
import { ChatComposerControls } from '@tribeplatform/react-components/Composer/components'
import { useComposer } from '@tribeplatform/react-components/Composer/hooks'
import { useAuthMember } from '@tribeplatform/react-sdk/hooks'
import { SvgIcon } from '@tribeplatform/react-ui-kit/Icon'
import { Text } from '@tribeplatform/react-ui-kit/Text'

import { T } from '../../i18n/index.js'
import { useChatContext } from '../index.js'
import {
  ChatAttachment,
  ComposerSubmitParams,
  DraftMessageType,
} from '../types.js'

import { ComposerAttachments } from './Composer/ComposerAttachments.js'

interface Props {
  onComposerSubmit?: (props: ComposerSubmitParams) => Promise<void>
  onKeyDown?: (event: KeyboardEvent) => void
  disabled: boolean
}

export const ChatComposer = ({
  onComposerSubmit,
  onKeyDown,
  disabled: disableControls,
}: Props) => {
  const virtualKeyboardVisibilityRef = useVirtualKeyboardVisibilityRef()
  const { editor } = useComposer()
  const {
    draftMessage,
    addAttachments,
    uploadingAttachments,
    cancelEditMessage,
    isEditingMessage,
    editorRef,
  } = useChatContext()
  const isUploading = uploadingAttachments.length > 0
  const { data: authMember } = useAuthMember()
  const { quickEnter } = authMember?.settings?.privateMessaging || {}
  const isEditing = draftMessage.type === DraftMessageType.Edit

  useEffect(() => {
    if (editorRef.current) {
      editorRef.current.commands.setContent(draftMessage.content)
    }
  }, [draftMessage.content, editorRef])

  const onAttachmentAdded = useCallback(
    async (files: File[], type: ChatAttachment['type']) => {
      if (type !== 'giphy') {
        addAttachments(files, type)
        return
      }

      // For giphys we want to send them right away without any other
      // content or attachments.
      onComposerSubmit({
        giphyFiles: files,
        isSendingGiphy: true,
      })
    },
    [onComposerSubmit, addAttachments],
  )

  useEffect(() => {
    if (!editor) {
      return
    }

    editor.setOptions({
      editorProps: {
        handleKeyDown: (_, event) => {
          if (event.key === 'Enter') {
            if (
              quickEnter &&
              virtualKeyboardVisibilityRef.current === false &&
              event.shiftKey === false
            ) {
              onComposerSubmit({})
              return true
            }

            if (event.metaKey === true || event.ctrlKey === true) {
              onComposerSubmit({})
              return true
            }
          }

          if (onKeyDown) {
            onKeyDown(event)
          }
          return false
        },
      },
    })
  }, [
    editor,
    onComposerSubmit,
    onKeyDown,
    quickEnter,
    virtualKeyboardVisibilityRef,
  ])

  const isEditorEmpty = editorRef.current?.getText().trim() === ''
  const disableSubmit =
    isEditorEmpty &&
    draftMessage.attachments.length === 0 &&
    draftMessage.embeds.length === 0

  return (
    <div className="p-3 pt-0">
      <div className="flex bg-surface flex-col min-h-[2rem] sm:min-h-[6rem] max-h-[30rem] gap-y-2 sm:gap-y-3 shadow-base border border-line-subdued rounded-base p-2">
        {isEditingMessage && (
          <div className="flex items-center gap-x-1 w-full border-b border-line-subdued pb-2">
            <Text className="text-xs font-light text-content-muted">
              <T id="Messaging.Composer.Editing" defaultMessage="Editing" />
            </Text>
            <SvgIcon name="edit-2" className="text-xs text-content-muted" />
          </div>
        )}
        <ComposerAttachments />
        <Composer onAttachmentAdded={onAttachmentAdded} />
        <div className="shrink-0">
          <ChatComposerControls
            disabledSubmit={disableSubmit}
            disableControls={disableControls}
            isLoading={isUploading}
            isEditingMessage={isEditing}
            onSubmit={() => onComposerSubmit({})}
            onCancelEdit={cancelEditMessage}
            onFileSelected={(files, type) => addAttachments(files, type)}
          />
        </div>
      </div>
    </div>
  )
}
